import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ROUTE_NAMES } from '@go-modules/services/router/router.service';
import { emailValidator } from '@go-modules/form-validator/email.validator/email.validator';
import { goPasswordValidator } from '@go-modules/form-validator/go-password/go-password.validator';
import { SignUpService } from '@go-modules/services/sign-up/sign-up.service';
import type { EmailAndPasswordData } from '@dashboard/app/interfaces/sign-up/email-and-password-data';
import { SignupMode } from '@dashboard/app/components/signup/signup-mode';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import type { CourseInvite } from '@dashboard/app/components/signup/course-invite';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { StateGoNamedParameters } from '../account-create/account-create.component';
import { RegionSelectorService } from '@dashboard/app/services/region-selector.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
	selector: 'email-and-password-form',
	templateUrl: './email-and-password-form.component.html',
	styleUrls: [
		'./email-and-password-form.component.scss',
		'./email-and-password-form-sm.component.scss',
		'./email-and-password-form-xs.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EmailAndPasswordFormComponent implements OnInit, OnDestroy {
	public static readonly NAME_MAX_LENGTH = 40;

	@Input() public isDashboard: boolean = false;
	@Output()
	public onComplete = new EventEmitter<EmailAndPasswordData>(); // eslint-disable-line @angular-eslint/no-output-on-prefix
	@Output()
	public onNavigate = new EventEmitter<StateGoNamedParameters>(); // eslint-disable-line @angular-eslint/no-output-on-prefix

	@Input()
	public set lockEmailWith (email: string) {
		this.lockEmailField = !!email;
		this.form.controls.email.setValue(email);
	}

	@Input()
	public mode: SignupMode;

	@Input()
	public courseInvite: CourseInvite;

	public form: UntypedFormGroup = new UntypedFormGroup({
		email: new UntypedFormControl(null, [Validators.required, emailValidator()]),
		password: new UntypedFormControl(null, [Validators.required, goPasswordValidator()])
	});

	public lockEmailField = false;
	public loading = false;
	public httpError: string | null = null;

	public SIGNUPMODES = SignupMode;

	public titleString;
	public currentRegionLabel: string | null = null;

	private destroy$ = new Subject<void>();
	private previousShowState = false;

	constructor (
		public featureFlag: NgxFeatureFlagService,
		private authService: NgxAuthService,
		private signUpService: SignUpService,
		private cdr: ChangeDetectorRef,
		private translate: TranslateService,
		private regionSelectorService: RegionSelectorService,
		@Inject('Window') private window: Window
	) {}

	public ngOnInit (): void {
		this.form.addControl('firstName', new UntypedFormControl(null, [
			Validators.required,
			Validators.maxLength(EmailAndPasswordFormComponent.NAME_MAX_LENGTH)
		]));
		this.form.addControl('lastName', new UntypedFormControl(null, [
			Validators.required,
			Validators.maxLength(EmailAndPasswordFormComponent.NAME_MAX_LENGTH)
		]));
		this.titleString =  this.mode === SignupMode.GUEST_REVIEW ? 'guest-review-signup_title' : 'common_welcome-to-goreact';

		if (this.isDashboard) {
			this.updateRegionLabel();

			this.regionSelectorService.showRegionSelector$
				.pipe(
					filter((show) => !show && this.previousShowState),
					takeUntil(this.destroy$)
				)
				.subscribe(() => {
					this.updateRegionLabel();
				});

			this.regionSelectorService.showRegionSelector$
				.pipe(takeUntil(this.destroy$))
				.subscribe((show) => {
					this.previousShowState = show;
				});
		}
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private updateRegionLabel (): void {
		const regionInfo = this.regionSelectorService.getCurrentRegionInfo();
		regionInfo.currentRegionLabel$.subscribe((label) => {
			this.currentRegionLabel = label;
			this.cdr.markForCheck();
		});
	}

	public navigateToLogin () {
		this.onNavigate.emit({to: ROUTE_NAMES.AUTH_LOGIN});
	}

	public openRegionSelector () {
		this.regionSelectorService.openRegionSelector();
	}

	public submit () {
		this.httpError = null;

		if (this.form.valid) {
			this.loading = true;
			this.cdr.detectChanges();
			this.signUpService.sendVerificationEmail(this.form.controls.email.value)
				.subscribe(() => {
					this.loading = false;
					this.onComplete.emit(this.form.value);
					this.cdr.detectChanges();
				}, (e: HttpErrorResponse) => {
					this.httpError = e.error.message;
					this.loading = false;
					this.cdr.detectChanges();
				});
		}
	}

	public signupWithSso (provider) {
		this.loading = true;

		const params: any = {};
		// Session retrieved from video share
		const urlParams = new URLSearchParams(this.window.location.search);
		const externalSession = urlParams.get('session');

		if (this.courseInvite) {
			params.groupConfirmed = true;
			if (this.courseInvite.course) {
				params.course = this.courseInvite.course.uuid;
			}
			if (this.courseInvite.invite) {
				params.invite = this.courseInvite.invite.uuid;
			}
		} else if (externalSession != null) {
			params.session = externalSession;
		}

		this.authService.getAuthUrl(provider, params)
			.subscribe({
				next: (res: any) => {
					this.window.location.href = res.url;
				},
				error: () => {
					this.httpError = this.translate.instant('common_an-unexpected-error-occurred');
					this.loading = false;
					this.cdr.detectChanges();
				}
			});
	}
}
